/**
 * Skin texture LOD and mobile quality management.
 *
 * Controls texture resolution, shader complexity, and feature availability
 * per quality tier. Used by WeaponSkinLoader to select appropriate texture
 * variants and by the viewmodel to enable/disable features like wear shaders
 * and StatTrak displays.
 *
 * Quality presets:
 * - mobile-low:    256² textures, no wear shader, no stickers, no StatTrak LED
 * - mobile-medium: 512² textures, simplified wear, 2 max stickers, text StatTrak
 * - mobile-high:   1024² textures, full wear, 4 stickers, full StatTrak
 * - desktop:       1024² textures, full features, 5 stickers, full StatTrak
 */

export type SkinQualityPreset = 'mobile-low' | 'mobile-medium' | 'mobile-high' | 'desktop';

export interface SkinQualityConfig {
  /** Texture resolution for skin maps (square, power of 2) */
  textureResolution: number;
  /** Whether the procedural wear shader is enabled */
  wearShaderEnabled: boolean;
  /** Whether wear shader uses simplified (2-octave) noise */
  wearShaderSimplified: boolean;
  /** Maximum stickers rendered per weapon (0 = disabled) */
  maxStickers: number;
  /** StatTrak display mode */
  statTrakMode: 'off' | 'text-only' | 'full-led';
  /** Whether emissive maps are loaded for custom-finish skins */
  emissiveMapsEnabled: boolean;
  /** Resolution multiplier for emissive maps (relative to textureResolution) */
  emissiveResolutionScale: number;
  /** Max number of remote player skins to load (0 = own only) */
  maxRemoteSkins: number;
  /** Max cached skin texture sets (LRU eviction beyond this) */
  maxCachedSkins: number;
  /** Whether to use ETC1S-only compression (faster decode, lower quality) */
  etc1sOnly: boolean;
  /** Texture format suffix for resolution variants (e.g. '-512' for 512px) */
  textureSuffix: string;
}

/** Quality configurations per preset. */
const QUALITY_CONFIGS: Record<SkinQualityPreset, SkinQualityConfig> = {
  'mobile-low': {
    textureResolution: 256,
    wearShaderEnabled: false,
    wearShaderSimplified: false,
    maxStickers: 0,
    statTrakMode: 'off',
    emissiveMapsEnabled: false,
    emissiveResolutionScale: 0,
    maxRemoteSkins: 0,
    maxCachedSkins: 2,
    etc1sOnly: true,
    textureSuffix: '-256',
  },
  'mobile-medium': {
    textureResolution: 512,
    wearShaderEnabled: true,
    wearShaderSimplified: true,
    maxStickers: 2,
    statTrakMode: 'text-only',
    emissiveMapsEnabled: false,
    emissiveResolutionScale: 0,
    maxRemoteSkins: 3,
    maxCachedSkins: 4,
    etc1sOnly: true,
    textureSuffix: '-512',
  },
  'mobile-high': {
    textureResolution: 1024,
    wearShaderEnabled: true,
    wearShaderSimplified: false,
    maxStickers: 4,
    statTrakMode: 'full-led',
    emissiveMapsEnabled: true,
    emissiveResolutionScale: 0.5,
    maxRemoteSkins: 5,
    maxCachedSkins: 6,
    etc1sOnly: false,
    textureSuffix: '',
  },
  'desktop': {
    textureResolution: 1024,
    wearShaderEnabled: true,
    wearShaderSimplified: false,
    maxStickers: 5,
    statTrakMode: 'full-led',
    emissiveMapsEnabled: true,
    emissiveResolutionScale: 1.0,
    maxRemoteSkins: 10,
    maxCachedSkins: 10,
    etc1sOnly: false,
    textureSuffix: '',
  },
};

/** Current active quality preset. */
let activePreset: SkinQualityPreset = 'desktop';

/**
 * Set the skin quality preset. Should be called during game initialization
 * based on device detection results.
 */
export function setSkinQualityPreset(preset: SkinQualityPreset): void {
  activePreset = preset;
}

/**
 * Get the current skin quality preset.
 */
export function getSkinQualityPreset(): SkinQualityPreset {
  return activePreset;
}

/**
 * Get the full quality configuration for the active preset.
 */
export function getSkinQualityConfig(): SkinQualityConfig {
  return QUALITY_CONFIGS[activePreset];
}

/**
 * Get the quality config for a specific preset (for UI previews).
 */
export function getSkinQualityConfigForPreset(preset: SkinQualityPreset): SkinQualityConfig {
  return QUALITY_CONFIGS[preset];
}

/**
 * Get the texture resolution for the current quality preset.
 */
export function getSkinTextureResolution(): number {
  return QUALITY_CONFIGS[activePreset].textureResolution;
}

/**
 * Resolve a skin texture path to the quality-appropriate variant.
 *
 * For mobile presets, appends a resolution suffix before the file extension.
 * Example: 'ak47/redline/color.ktx2' -> 'ak47/redline/color-512.ktx2'
 *
 * Desktop (1024) paths have no suffix (they are the base files).
 *
 * @param basePath - Original texture path from skin manifest
 * @returns Resolved path for current quality preset
 */
export function resolveTexturePath(basePath: string): string {
  const suffix = QUALITY_CONFIGS[activePreset].textureSuffix;
  if (!suffix) return basePath; // Desktop uses base paths

  // Insert suffix before file extension
  const dotIndex = basePath.lastIndexOf('.');
  if (dotIndex === -1) return basePath + suffix;
  return basePath.slice(0, dotIndex) + suffix + basePath.slice(dotIndex);
}

/**
 * Check if a skin feature is available at the current quality level.
 */
export function isSkinFeatureEnabled(feature: 'wear-shader' | 'stickers' | 'stattrak-led' | 'emissive' | 'remote-skins'): boolean {
  const config = QUALITY_CONFIGS[activePreset];
  switch (feature) {
    case 'wear-shader': return config.wearShaderEnabled;
    case 'stickers': return config.maxStickers > 0;
    case 'stattrak-led': return config.statTrakMode === 'full-led';
    case 'emissive': return config.emissiveMapsEnabled;
    case 'remote-skins': return config.maxRemoteSkins > 0;
  }
}

/**
 * Estimate GPU memory usage for a skin texture set at the given resolution.
 * Useful for budget tracking and deciding whether to load additional skins.
 *
 * @param resolution - Texture resolution (one side, square)
 * @param hasEmissive - Whether the skin has an emissive map
 * @returns Estimated GPU memory in bytes
 */
export function estimateSkinMemory(resolution: number, hasEmissive: boolean): number {
  // Compressed texture size estimate (Basis Universal):
  // ETC1S: ~0.5 bytes/pixel, UASTC: ~1.0 bytes/pixel
  // Each map: resolution² x bytes_per_pixel x mip_factor(1.33)
  const config = QUALITY_CONFIGS[activePreset];
  const pixelCount = resolution * resolution;
  const mipFactor = 1.33;

  // Color (ETC1S): ~0.5 B/px
  let total = pixelCount * 0.5 * mipFactor;
  // Normal (UASTC or ETC1S depending on preset): 0.5-1.0 B/px
  total += pixelCount * (config.etc1sOnly ? 0.5 : 1.0) * mipFactor;
  // ORM (ETC1S): ~0.5 B/px
  total += pixelCount * 0.5 * mipFactor;

  // Emissive (optional)
  if (hasEmissive && config.emissiveMapsEnabled) {
    const emissiveRes = resolution * config.emissiveResolutionScale;
    total += emissiveRes * emissiveRes * 0.5 * mipFactor;
  }

  return Math.ceil(total);
}

/**
 * Get the maximum number of skin texture sets that should be cached
 * for the current quality preset.
 */
export function getMaxCachedSkins(): number {
  return QUALITY_CONFIGS[activePreset].maxCachedSkins;
}

/**
 * Detect the appropriate skin quality preset based on device capabilities.
 * Uses the same detection as the game's quality system.
 *
 * @param isMobile - Whether the device is mobile
 * @param gpuTier - GPU tier from detect-gpu (0-3)
 * @returns Recommended skin quality preset
 */
export function detectSkinQualityPreset(isMobile: boolean, gpuTier: number): SkinQualityPreset {
  if (!isMobile) return 'desktop';

  if (gpuTier <= 1) return 'mobile-low';
  if (gpuTier === 2) return 'mobile-medium';
  return 'mobile-high';
}
